import { Injectable } from "@angular/core";
import { AnyAccountJwtVOSpecs, HostSpecs, RoomId } from "@common/domain";
import { select, Store } from "@ngrx/store";
import { AccountJwtService } from "@youm-front/account-jwt/data-access";
import {
  ConferenceStreamsListService,
  LocalConferenceStreamsListService
} from "@youm-front/conference-stream/data-access";
import {
  RoomAccountActions,
  RoomActions,
  RoomSelectors,
  RoomStreamActions
} from "@youm-front/room/data-access";
import {
  RoomAccountDeleteCmd,
  RoomStreamDeleteCmd,
  RoomStreamRestartCmd,
  RoomStreamVO
} from "@youm-front/room/domain";
import { combineLatest, Observable } from "rxjs";
import { distinctUntilChanged, map, shareReplay } from "rxjs/operators";
import {
  ConferenceStream,
  LocalCamMicConferenceStream
} from "youm-lib/conference";

@Injectable()
export class RoomFacade {
  readonly room$ = this._store.pipe(select(RoomSelectors.room));
  readonly isLoading$: Observable<boolean>;
  readonly streams$: Observable<RoomStreamVO[]>;
  readonly activeStreams$: Observable<RoomStreamVO[]>;
  readonly isHost$: Observable<boolean>;
  readonly streamsMap$ = this._store.pipe(select(RoomSelectors.roomStreamsMap));

  constructor(
    private readonly _store: Store,
    private readonly localConferenceStreamsListService: LocalConferenceStreamsListService,
    private readonly conferenceStreamsListService: ConferenceStreamsListService,
    private readonly accountJwtService: AccountJwtService
  ) {
    this.isLoading$ = this._store.pipe(select(RoomSelectors.isLoading));
    this.streams$ = this._store.pipe(select(RoomSelectors.roomStreams));
    this.activeStreams$ = this._store.pipe(
      select(RoomSelectors.roomActiveStreams)
    );

    this.isHost$ = combineLatest([
      this.accountJwtService.anyAccountJwtVO$.pipe(
        distinctUntilChanged(AnyAccountJwtVOSpecs.cmp)
      ),
      this.room$.pipe(
        distinctUntilChanged((lhs, rhs) => lhs?.value.id === rhs?.value.id)
      )
    ]).pipe(
      map((_) => HostSpecs.isHost(_[0], _[1]?.value.ownerId)),
      shareReplay(1)
    );
  }

  get activeCStreams$() {
    return combineLatest([
      this.activeStreams$,
      this.conferenceStreamsListService.cStreamsMap$
    ]).pipe(
      map(([roomActiveStreams, streamsMap]) =>
        roomActiveStreams.reduce((acc, roomActiveStream) => {
          const stream = streamsMap.get(roomActiveStream.value.streamId);
          return stream ? [...acc, stream] : acc;
        }, [] as ConferenceStream[])
      )
    );
  }

  get localCamMicStream$(): Observable<null | LocalCamMicConferenceStream> {
    return this.localConferenceStreamsListService.streams$.pipe(
      map((_) => _.find((_) => _.isCamMic)),
      map((_) => (_ ? (_ as LocalCamMicConferenceStream) : null))
    );
  }

  get hasScreenShare$() {
    return this.localConferenceStreamsListService.streams$.pipe(
      map((_) => !!_.find((_) => _.isScreen))
    );
  }

  get roomLiveAt$() {
    return this.room$.pipe(
      map((_) => (_?.value.isLive ? _?.value.liveAt || null : null))
    );
  }

  listRoomStreams(roomId: RoomId) {
    this._store.dispatch(RoomActions.listRoomStreams({ roomId }));
  }

  toggleStreamSound(stream: RoomStreamVO) {
    this._store.dispatch(RoomActions.toggleStreamSound({ stream }));
  }

  toggleLocalScreenStream() {
    this._store.dispatch(RoomActions.toggleScreenStream());
  }

  toggleLocalVideoStream() {
    this._store.dispatch(RoomActions.toggleLocalVideoStream());
  }

  toggleLocalAudioStream() {
    this._store.dispatch(RoomActions.toggleLocalAudioStream());
  }

  publishLocalVideoFileStream(file: File) {
    this._store.dispatch(RoomActions.publishLocalVideoStream({ file }));
  }

  publishLocalAudioFileStream(file: File) {
    this._store.dispatch(RoomActions.publishLocalAudioStream({ file }));
  }

  roomAccountDelete(roomAccountDeleteCmd: RoomAccountDeleteCmd) {
    this._store.dispatch(
      RoomAccountActions.roomAccountDelete({
        roomAccountDeleteCmd
      })
    );
  }

  roomStreamDelete(roomStreamDeleteCmd: RoomStreamDeleteCmd) {
    this._store.dispatch(
      RoomStreamActions.roomStreamDelete({
        roomStreamDeleteCmd
      })
    );
  }

  roomStreamRestart(roomStreamRestartCmd: RoomStreamRestartCmd) {
    this._store.dispatch(
      RoomStreamActions.roomStreamRestart({
        roomStreamRestartCmd
      })
    );
  }

  startLiveStream(roomId: RoomId) {
    this._store.dispatch(RoomActions.startLiveStream({ roomId }));
  }

  stopLiveStream(roomId: RoomId) {
    this._store.dispatch(RoomActions.stopLiveStream({ roomId }));
  }
}
